import { lazy } from 'react';
import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
} from 'react-router-dom';

import { FEATURES } from '@/app/config/features';
import { AdminLayout } from '@/app/layouts/admin-layout';
import { AuthLayout } from '@/app/layouts/auth-layout';
import { BackofficeLayout } from '@/app/layouts/backoffice-layout';
import { AuthGuard } from '@/app/guards/auth-guard';
import { BackofficeAuthGuard } from '@/app/guards/backoffice-auth-guard';
import { BackofficeGuestGuard } from '@/app/guards/backoffice-guest-guard';
import { GuestGuard } from '@/app/guards/guest-guard';
import { ROUTES } from '@/app/router/route-constants';
import { withSuspense } from '@/app/router/route-utils';
import { PublicBookingFormSkeleton } from '@/features/public-booking-form/components/public-booking-form-skeleton';
import { MenuPreviewSkeleton } from '@/features/menu/components/menu-preview/menu-preview-skeleton';
import { PublicDcfFormSkeleton } from '@/features/data-collection-forms/components/public-dcf-form-skeleton';
import { PublicPromoFormSkeleton } from '@/features/promotion/components/public-promo-form-skeleton';
import { PublicFidelityFormSkeleton } from '@/features/fidelity/components/public-fidelity-form-skeleton';
import { ErrorPage } from '@/pages/error-page';
import { NotFoundPage } from '@/pages/not-found-page';
import { UnauthorizedPage } from '@/pages/unauthorized-page';

const LoginPage = lazy(() =>
  import('@/features/auth/pages/login-page').then((m) => ({
    default: m.LoginPage,
  })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/dashboard-page').then((m) => ({
    default: m.DashboardPage,
  })),
);
const SurveysPage = lazy(() =>
  import('@/features/surveys/surveys-page').then((m) => ({
    default: m.SurveysPage,
  })),
);
const PublicSurveyPage = lazy(() =>
  import('@/features/surveys/public-survey-page').then((m) => ({
    default: m.PublicSurveyPage,
  })),
);
const AutomationPage = lazy(() =>
  import('@/features/automation/pages/automation-page').then((m) => ({
    default: m.AutomationPage,
  })),
);

const UsersPage = lazy(() =>
  import('@/features/users/pages/users-page').then((m) => ({
    default: m.UsersPage,
  })),
);

const UserDetailPage = lazy(() =>
  import('@/features/users/pages/user-detail-page').then((m) => ({
    default: m.UserDetailPage,
  })),
);

const SettingsPage = lazy(() =>
  import('@/features/settings/pages/settings-blank-pages').then((m) => ({
    default: m.SettingsRedirectPage,
  })),
);

const UserSettingsPage = lazy(() =>
  import('@/features/settings/pages/user-settings-page').then((m) => ({
    default: m.UserSettingsPage,
  })),
);

const LocalConfigurationPage = lazy(() =>
  import('@/features/local-configuration/pages/local-configuration-page').then(
    (m) => ({
      default: m.LocalConfigurationPage,
    }),
  ),
);

const SettingsConfigureWhatsAppPage = lazy(() =>
  import('@/features/configure-whatsapp/pages/configure-whatsapp-page').then(
    (m) => ({
      default: m.ConfigureWhatsAppPage,
    }),
  ),
);

const SettingsMessagesPage = lazy(() =>
  import('@/features/messages/pages/messages-page').then((m) => ({
    default: m.MessagesPage,
  })),
);

const SettingsMessageDetailPage = lazy(() =>
  import('@/features/messages/pages/message-detail-page').then((m) => ({
    default: m.MessageDetailPage,
  })),
);

const SettingsOfficialWhatsAppChannelPage = lazy(() =>
  import('@/features/configure-whatsapp-official/pages/configure-whatsapp-official-page').then(
    (m) => ({
      default: m.ConfigureWhatsAppOfficialPage,
    }),
  ),
);

const SettingsTemplatesPage = lazy(() =>
  import('@/features/whatsapp-templates/pages/whatsapp-templates-page').then(
    (m) => ({
      default: m.WhatsAppTemplatesPage,
    }),
  ),
);

const SettingsTemplatesCreatePage = lazy(() =>
  import('@/features/whatsapp-templates/pages/whatsapp-template-create-page').then(
    (m) => ({
      default: m.WhatsAppTemplateCreatePage,
    }),
  ),
);

const SettingsWhatsAppOfficialPage = lazy(() =>
  import('@/features/whatsapp-official/pages/whatsapp-official-page').then(
    (m) => ({
      default: m.WhatsAppOfficialPage,
    }),
  ),
);

const StripeConfigurationPage = lazy(() =>
  import('@/features/stripe/pages/stripe-configuration-page').then((m) => ({
    default: m.StripeConfigurationPage,
  })),
);

const StripeTransactionsPage = lazy(() =>
  import('@/features/stripe/pages/stripe-transactions-page').then((m) => ({
    default: m.StripeTransactionsPage,
  })),
);

const NotificationsPage = lazy(() =>
  import('@/features/notifications/pages/notifications-page').then((m) => ({
    default: m.NotificationsPage,
  })),
);

const ReportsPage = lazy(() =>
  import('@/pages/reports-page').then((m) => ({
    default: m.ReportsPage,
  })),
);

const BookingHistoryPage = lazy(() =>
  import('@/features/bookings/pages/booking-history-page').then((m) => ({
    default: m.BookingHistoryPage,
  })),
);

const BookingHistoryDetailsPage = lazy(() =>
  import('@/features/bookings/pages/booking-history-details-page').then(
    (m) => ({
      default: m.BookingHistoryDetailsPage,
    }),
  ),
);

const FormEditPage = lazy(() =>
  import('@/features/forms/pages/form-edit-page').then((m) => ({
    default: m.FormEditPage,
  })),
);

const FormPage = lazy(() =>
  import('@/features/forms/pages/form-page').then((m) => ({
    default: m.FormPage,
  })),
);

const ExtraServicesPage = lazy(() =>
  import('@/features/extra-services/pages/extra-services-page').then((m) => ({
    default: m.ExtraServicesPage,
  })),
);

const FormMenuPage = lazy(() =>
  import('@/features/form-menu/pages/form-menu-page').then((m) => ({
    default: m.FormMenuPage,
  })),
);

const CreateFormMenuPage = lazy(() =>
  import('@/features/form-menu/pages/create-form-menu-page').then((m) => ({
    default: m.CreateFormMenuPage,
  })),
);

const EditFormMenuPage = lazy(() =>
  import('@/features/form-menu/pages/edit-form-menu-page').then((m) => ({
    default: m.EditFormMenuPage,
  })),
);

const EventsPage = lazy(() =>
  import('@/features/events/pages/events-page').then((m) => ({
    default: m.EventsPage,
  })),
);

const CreateEventPage = lazy(() =>
  import('@/features/events/pages/create-event-page').then((m) => ({
    default: m.CreateEventPage,
  })),
);

const EditEventPage = lazy(() =>
  import('@/features/events/pages/edit-event-page').then((m) => ({
    default: m.EditEventPage,
  })),
);

const MyCustomersPage = lazy(() =>
  import('@/features/customers/pages/my-customers-page').then((m) => ({
    default: m.MyCustomersPage,
  })),
);

const DataCollectionFormsPage = lazy(() =>
  import('@/features/data-collection-forms/pages/page').then((m) => ({
    default: m.Page,
  })),
);

const EditDataCollectionFormPage = lazy(() =>
  import('@/features/data-collection-forms/pages/edit-data-collection-form-page').then(
    (m) => ({
      default: m.EditDataCollectionFormPage,
    }),
  ),
);

const BulkMessagePage = lazy(() =>
  import('@/features/bulk-message/pages/bulk-message-page').then((m) => ({
    default: m.BulkMessagePage,
  })),
);

const BulkMessageEditPage = lazy(() =>
  import('@/features/bulk-message/pages/bulk-message-edit-page').then((m) => ({
    default: m.BulkMessageEditPage,
  })),
);

const BulkMessageSelectCustomersPage = lazy(() =>
  import('@/features/bulk-message/pages/bulk-message-select-customers-page').then(
    (m) => ({
      default: m.BulkMessageSelectCustomersPage,
    }),
  ),
);

const BulkMessageDetailsPage = lazy(() =>
  import('@/features/bulk-message/pages/bulk-message-details-page').then(
    (m) => ({
      default: m.BulkMessageDetailsPage,
    }),
  ),
);

// const CustomerDetailsPage = lazy(() =>
//   import('@/features/customers/pages/customer-deatils-page').then((m) => ({
//     default: m.CustomerDetailsPage,
//   })),
// );

const GiftCardsPage = lazy(() =>
  import('@/features/gift-cards/pages/gift-cards-page').then((m) => ({
    default: m.GiftCardsPage,
  })),
);

const DiscountCardsPage = lazy(() =>
  import('@/features/fidelity/pages/discount-cards-page').then((m) => ({
    default: m.DiscountCardsPage,
  })),
);

const FidelityPrizeCatalogPage = lazy(() =>
  import('@/features/fidelity/pages/fidelity-prize-catalog-page').then((m) => ({
    default: m.FidelityPrizeCatalogPage,
  })),
);

const FidelityPage = lazy(() =>
  import('@/features/fidelity/pages/fidelity-page').then((m) => ({
    default: m.FidelityPage,
  })),
);

const FidelityEditPage = lazy(() =>
  import('@/features/fidelity/pages/fidelity-edit-page').then((m) => ({
    default: m.FidelityEditPage,
  })),
);

const FidelityCreateCardPage = lazy(() =>
  import('@/features/fidelity/pages/fidelity-create-card-page').then((m) => ({
    default: m.FidelityCreateCardPage,
  })),
);

const FidelityCardDetailsPage = lazy(() =>
  import('@/features/fidelity/pages/fidelity-card-details-page').then((m) => ({
    default: m.FidelityCardDetailsPage,
  })),
);

const PromotionsPage = lazy(() =>
  import('@/features/promotion/pages/promotions-page').then((m) => ({
    default: m.PromotionsPage,
  })),
);

const PromotionEditPage = lazy(() =>
  import('@/features/promotion/pages/promotion-edit-page').then((m) => ({
    default: m.PromotionEditPage,
  })),
);

const LandingPagesPage = lazy(() =>
  import('@/features/landingPages/pages/landing-page').then((m) => ({
    default: m.LandingPagesPage,
  })),
);

const EditLandingPage = lazy(() =>
  import('@/features/landingPages/pages/edit-landing-page').then((m) => ({
    default: m.EditLandingPage,
  })),
);

const PublicLandingPage = lazy(() =>
  import('@/features/landingPages/pages/public-landing-page').then((m) => ({
    default: m.PublicLandingPage,
  })),
);

const IngredientsPage = lazy(() =>
  import('@/features/menu/pages/ingredients-page').then((m) => ({
    default: m.IngredientsPage,
  })),
);
const SuppliersPage = lazy(() =>
  import('@/features/supplier-orders/pages/suppliers-page').then((m) => ({
    default: m.SuppliersPage,
  })),
);
const SupplierProductsPage = lazy(() =>
  import('@/features/supplier-orders/pages/supplier-products-page').then(
    (m) => ({ default: m.SupplierProductsPage }),
  ),
);
const SupplierOrderHistoryPage = lazy(() =>
  import('@/features/supplier-orders/pages/supplier-order-history-page').then(
    (m) => ({ default: m.SupplierOrderHistoryPage }),
  ),
);
const DeliveryOrdersPage = lazy(() =>
  import('@/features/restaurant-orders/pages/delivery-orders-page').then(
    (m) => ({ default: m.DeliveryOrdersPage }),
  ),
);
const TakeawayOrdersPage = lazy(() =>
  import('@/features/restaurant-orders/pages/takeaway-orders-page').then(
    (m) => ({ default: m.TakeawayOrdersPage }),
  ),
);
const SupplierOrdersPage = lazy(() =>
  import('@/features/supplier-orders/pages/supplier-orders-page').then((m) => ({
    default: m.SupplierOrdersPage,
  })),
);
const SupplierOrderDetailPage = lazy(() =>
  import('@/features/supplier-orders/pages/supplier-order-detail-page').then(
    (m) => ({ default: m.SupplierOrderDetailPage }),
  ),
);
const MenuPage = lazy(() =>
  import('@/features/menu/pages/menu-page').then((m) => ({
    default: m.MenuPage,
  })),
);

const MenuEditPage = lazy(() =>
  import('@/features/menu/pages/menu-edit-page').then((m) => ({
    default: m.MenuEditPage,
  })),
);

const MenuPreviewPage = lazy(() =>
  import('@/features/menu/pages/menu-preview-page').then((m) => ({
    default: m.MenuPreviewPage,
  })),
);

const LegacyMenuRedirect = lazy(() =>
  import('@/features/menu/pages/legacy-menu-redirect').then((m) => ({
    default: m.LegacyMenuRedirect,
  })),
);

const TagPage = lazy(() =>
  import('@/features/tags/pages/tags-page').then((m) => ({
    default: m.TagsPage,
  })),
);

const PublicRelationPage = lazy(() =>
  import('@/features/public-relations/pages/public-relations-page').then(
    (m) => ({
      default: m.PublicRelationsPage,
    }),
  ),
);

const CollaboratorsPage = lazy(() =>
  import('@/features/staff/pages/collaborators-page').then((m) => ({
    default: m.CollaboratorsPage,
  })),
);

const CollaboratorDetailsPage = lazy(() =>
  import('@/features/staff/pages/collaborator-details-page').then((m) => ({
    default: m.CollaboratorDetailsPage,
  })),
);

const CollaboratorReportsPage = lazy(() =>
  import('@/features/staff/pages/collaborator-reports-page').then((m) => ({
    default: m.CollaboratorReportsPage,
  })),
);

const BackofficeLoginPage = lazy(() =>
  import('@/features/backoffice2020/pages/backoffice-login-page').then((m) => ({
    default: m.BackofficeLoginPage,
  })),
);

const Backoffice2020Page = lazy(() =>
  import('@/features/backoffice2020/pages/backoffice2020-page').then((m) => ({
    default: m.Backoffice2020Page,
  })),
);

const PublicRestaurantOrderingPage = lazy(() =>
  import('@/features/restaurant-orders/pages/public-restaurant-ordering-page').then(
    (m) => ({ default: m.PublicRestaurantOrderingPage }),
  ),
);
const PublicWalletPage = lazy(() =>
  import('@/features/wallet/pages/public-gift-card-page').then((m) => ({
    default: m.PublicWalletPage,
  })),
);

const PublicSupplierOrderPage = lazy(() =>
  import('@/features/supplier-orders/pages/public-supplier-order-page').then(
    (m) => ({ default: m.PublicSupplierOrderPage }),
  ),
);

const PublicPromoPage = lazy(() =>
  import('@/features/promotion/pages/public-promo-page').then((m) => ({
    default: m.PublicPromoPage,
  })),
);

const PublicCatalogCardPage = lazy(() =>
  import('@/features/fidelity/pages/public-catalog-card-page').then((m) => ({
    default: m.PublicCatalogCardPage,
  })),
);

const PublicFidelityPage = lazy(() =>
  import('@/features/fidelity/pages/public-fidelity-page').then((m) => ({
    default: m.PublicFidelityPage,
  })),
);

const PublicDataCollectionFormPage = lazy(() =>
  import('@/features/data-collection-forms/pages/public-data-collection-form-page').then(
    (m) => ({
      default: m.PublicDataCollectionFormPage,
    }),
  ),
);

const PublicBookingFormPage = lazy(() =>
  import('@/features/public-booking-form/pages/public-booking-form-page').then(
    (m) => ({
      default: m.PublicBookingFormPage,
    }),
  ),
);

const LegacyPublicBookingFormRedirect = lazy(() =>
  import('@/features/public-booking-form/pages/legacy-public-booking-form-redirect').then(
    (m) => ({
      default: m.LegacyPublicBookingFormRedirect,
    }),
  ),
);

const PublicCardDetailsPage = lazy(() =>
  import('@/features/public-booking-form/pages/public-card-details-page').then(
    (m) => ({
      default: m.PublicCardDetailsPage,
    }),
  ),
);

const PublicBookingUpdatePage = lazy(() =>
  import('@/features/public-booking-update/pages/public-booking-update-page').then(
    (m) => ({
      default: m.PublicBookingUpdatePage,
    }),
  ),
);

const LegacyPublicBookingUpdateRedirect = lazy(() =>
  import('@/features/public-booking-update/pages/legacy-public-booking-update-redirect').then(
    (m) => ({
      default: m.LegacyPublicBookingUpdateRedirect,
    }),
  ),
);

const BackofficeEditActivityPage = lazy(() =>
  import('@/features/backoffice2020/pages/backoffice-edit-activity-page').then(
    (m) => ({
      default: m.BackofficeEditActivityPage,
    }),
  ),
);

const HomeLandingPage = lazy(() =>
  import('@/features/home/pages/home-landing-page').then((m) => ({
    default: m.HomeLandingPage,
  })),
);
const FaqPage = lazy(() =>
  import('@/features/home/pages/faq-page').then((m) => ({
    default: m.FaqPage,
  })),
);

const PrivacyPage = lazy(() =>
  import('@/features/home/pages/privacy-page').then((m) => ({
    default: m.PrivacyPage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/features/home/pages/privacy-policy-page').then((m) => ({
    default: m.PrivacyPolicyPage,
  })),
);

const MarketingLayout = lazy(() =>
  import('@/features/home/layouts/marketing-layout').then((m) => ({
    default: m.MarketingLayout,
  })),
);

const routes: RouteObject[] = [
  {
    element: withSuspense(<MarketingLayout />),
    children: [
      {
        path: ROUTES.HOME,
        element: withSuspense(<HomeLandingPage />),
      },
      {
        path: ROUTES.FAQ,
        element: withSuspense(<FaqPage />),
      },
      {
        path: ROUTES.PRIVACY_POLICY,
        element: withSuspense(<PrivacyPolicyPage />),
      },
      {
        path: ROUTES.PRIVACY,
        element: <Navigate to={ROUTES.PRIVACY_POLICY} replace />,
      },
      {
        path: ROUTES.TERMS,
        element: withSuspense(<PrivacyPage />),
      },
    ],
  },
  {
    element: <AuthGuard />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          {
            path: ROUTES.DASHBOARD,
            element: withSuspense(<DashboardPage />),
          },
          {
            path: ROUTES.USERS,
            element: withSuspense(<UsersPage />),
          },
          {
            path: ROUTES.USER_DETAIL,
            element: withSuspense(<UserDetailPage />),
          },
          {
            path: ROUTES.SETTINGS,
            element: withSuspense(<SettingsPage />),
          },
          {
            path: ROUTES.USER_SETTINGS,
            element: withSuspense(<UserSettingsPage />),
          },
          {
            path: ROUTES.SETTINGS_LOCAL_CONFIGURATION,
            element: withSuspense(<LocalConfigurationPage />),
          },
          {
            path: ROUTES.SETTINGS_CONFIGURE_WHATSAPP,
            element: withSuspense(<SettingsConfigureWhatsAppPage />),
          },
          {
            path: ROUTES.SETTINGS_MESSAGES,
            element: withSuspense(<SettingsMessagesPage />),
          },
          {
            path: ROUTES.SETTINGS_MESSAGES_DETAIL,
            element: withSuspense(<SettingsMessageDetailPage />),
          },
          {
            path: ROUTES.SETTINGS_OFFICIAL_WHATSAPP_CHANNEL,
            element: withSuspense(<SettingsOfficialWhatsAppChannelPage />),
          },
          {
            path: ROUTES.SETTINGS_TEMPLATES,
            element: withSuspense(<SettingsTemplatesPage />),
          },
          {
            path: ROUTES.SETTINGS_TEMPLATES_NEW,
            element: withSuspense(<SettingsTemplatesCreatePage />),
          },
          {
            path: ROUTES.SETTINGS_WHATSAPP_OFFICIAL,
            element: withSuspense(<SettingsWhatsAppOfficialPage />),
          },
          {
            path: ROUTES.SETTINGS_STRIPE_CONFIGURATION,
            element: withSuspense(<StripeConfigurationPage />),
          },
          {
            path: ROUTES.SETTINGS_STRIPE_TRANSACTIONS,
            element: withSuspense(<StripeTransactionsPage />),
          },
          {
            path: ROUTES.REPORTS,
            element: withSuspense(<ReportsPage />),
          },
          {
            path: ROUTES.BOOKING_HISTORY,
            element: withSuspense(<BookingHistoryPage />),
          },
          {
            path: ROUTES.BOOKING_HISTORY_DETAILS,
            element: withSuspense(<BookingHistoryDetailsPage />),
          },
          {
            path: ROUTES.FORM_HISTORY,
            element: withSuspense(<FormPage />),
          },
          {
            path: ROUTES.FORM_EDIT,
            element: withSuspense(<FormEditPage />),
          },
          {
            path: ROUTES.EXTRA_SERVICE_AND_PRODUCT,
            element: withSuspense(<ExtraServicesPage />),
          },
          {
            path: ROUTES.FORM_MENU,
            element: withSuspense(<FormMenuPage />),
          },
          {
            path: ROUTES.FORM_MENU_CREATE,
            element: withSuspense(<CreateFormMenuPage />),
          },
          {
            path: ROUTES.FORM_MENU_EDIT,
            element: withSuspense(<EditFormMenuPage />),
          },
          {
            path: ROUTES.EVENTS,
            element: withSuspense(<EventsPage />),
          },
          {
            path: ROUTES.EVENTS_CREATE,
            element: withSuspense(<CreateEventPage />),
          },
          {
            path: ROUTES.EVENTS_EDIT,
            element: withSuspense(<EditEventPage />),
          },
          {
            path: ROUTES.MY_CUSTOMERS,
            element: withSuspense(<MyCustomersPage />),
          },
          {
            path: ROUTES.DATA_COLLECTION_FORMS,
            element: withSuspense(<DataCollectionFormsPage />),
          },
          {
            path: ROUTES.DATA_COLLECTION_FORM_EDIT,
            element: withSuspense(<EditDataCollectionFormPage />),
          },
          // {
          //   path: ROUTES.CUSTOMER_DETAILS,
          //   element: withSuspense(<CustomerDetailsPage />),
          // },
          {
            path: ROUTES.BULK_MESSAGE,
            element: withSuspense(<BulkMessagePage />),
          },
          {
            path: ROUTES.SURVEYS,
            element: withSuspense(<SurveysPage />),
          },
          {
            path: ROUTES.AUTOMATION,
            element: <Navigate to={ROUTES.AUTOMATION_DASHBOARD} replace />,
          },
          {
            path: ROUTES.AUTOMATION_CONFIGURATION,
            element: FEATURES.AUTOMATION ? (
              withSuspense(<AutomationPage view="editor" />)
            ) : (
              <Navigate to={ROUTES.MY_CUSTOMERS} replace />
            ),
          },
          {
            path: ROUTES.AUTOMATION_DASHBOARD,
            element: FEATURES.AUTOMATION ? (
              withSuspense(<AutomationPage view="dashboard" />)
            ) : (
              <Navigate to={ROUTES.MY_CUSTOMERS} replace />
            ),
          },
          {
            path: ROUTES.BULK_MESSAGE_EDIT,
            element: withSuspense(<BulkMessageEditPage />),
          },
          {
            path: ROUTES.BULK_MESSAGE_DETAILS,
            element: withSuspense(<BulkMessageDetailsPage />),
          },
          {
            path: ROUTES.BULK_MESSAGE_SELECT_CUSTOMERS,
            element: withSuspense(<BulkMessageSelectCustomersPage />),
          },
          { path: ROUTES.GIFT_CARDS, element: withSuspense(<GiftCardsPage />) },
          {
            path: ROUTES.FIDELITY,
            element: withSuspense(<FidelityPage />),
          },
          {
            path: ROUTES.DISCOUNT_CARDS,
            element: withSuspense(<DiscountCardsPage />),
          },
          {
            path: ROUTES.FIDELITY_PRIZE_CATALOG,
            element: withSuspense(<FidelityPrizeCatalogPage />),
          },
          {
            path: ROUTES.FIDELITY_EDIT,
            element: withSuspense(<FidelityEditPage />),
          },
          {
            path: ROUTES.FIDELITY_CREATE_CARD,
            element: withSuspense(<FidelityCreateCardPage />),
          },
          {
            path: ROUTES.FIDELITY_CARD_DETAILS,
            element: withSuspense(<FidelityCardDetailsPage />),
          },
          {
            path: ROUTES.PROMOTIONS,
            element: withSuspense(<PromotionsPage />),
          },
          {
            path: ROUTES.PROMOTION_EDIT,
            element: withSuspense(<PromotionEditPage />),
          },
          {
            path: ROUTES.LANDING_PAGES,
            element: withSuspense(<LandingPagesPage />),
          },
          {
            path: ROUTES.LANDING_PAGE_EDIT,
            element: withSuspense(<EditLandingPage />),
          },
          {
            path: ROUTES.ORDERING_DRIVERS,
            element: withSuspense(<DeliveryOrdersPage drivers />),
          },
          {
            path: ROUTES.ORDERING_CONFIGURATION,
            element: withSuspense(<DeliveryOrdersPage configuration />),
          },
          {
            path: ROUTES.ORDER_HISTORY,
            element: withSuspense(<DeliveryOrdersPage history />),
          },
          {
            path: ROUTES.DELIVERY_ORDERS,
            element: withSuspense(<DeliveryOrdersPage />),
          },
          {
            path: ROUTES.TAKEAWAY_ORDERS,
            element: withSuspense(<TakeawayOrdersPage />),
          },
          { path: ROUTES.SUPPLIERS, element: withSuspense(<SuppliersPage />) },
          {
            path: ROUTES.SUPPLIER_PRODUCTS,
            element: withSuspense(<SupplierProductsPage />),
          },
          {
            path: ROUTES.SUPPLIER_ORDERS,
            element: withSuspense(<SupplierOrdersPage />),
          },
          {
            path: ROUTES.SUPPLIER_ORDER_HISTORY,
            element: withSuspense(<SupplierOrderHistoryPage />),
          },
          {
            path: ROUTES.SUPPLIER_ORDER_DETAIL,
            element: withSuspense(<SupplierOrderDetailPage />),
          },
          {
            path: '/menu/ingredients',
            element: withSuspense(<IngredientsPage />),
          },
          {
            path: ROUTES.MENU,
            element: withSuspense(<MenuPage />),
          },
          {
            path: ROUTES.MENU_EDIT,
            element: withSuspense(<MenuEditPage />),
          },
          {
            path: ROUTES.TAGS,
            element: withSuspense(<TagPage />),
          },
          {
            path: ROUTES.PUBLIC_RELATIONS,
            element: withSuspense(<PublicRelationPage />),
          },
          {
            path: ROUTES.COLLABORATOR_ROLES,
            // Role APIs are not deployed yet; keep direct links away from the page.
            element: <Navigate to={ROUTES.COLLABORATORS} replace />,
          },
          {
            path: ROUTES.COLLABORATORS,
            element: withSuspense(<CollaboratorsPage />),
          },
          {
            path: ROUTES.COLLABORATOR_DETAILS,
            element: withSuspense(<CollaboratorDetailsPage />),
          },
          {
            path: ROUTES.COLLABORATOR_REPORTS,
            element: withSuspense(<CollaboratorReportsPage />),
          },
          {
            path: ROUTES.NOTIFICATIONS,
            element: withSuspense(<NotificationsPage />),
          },
        ],
      },
    ],
  },
  {
    path: '/delivery/:code',
    element: withSuspense(<PublicRestaurantOrderingPage />),
  },
  {
    path: '/takeaway/:code',
    element: withSuspense(
      <PublicRestaurantOrderingPage preferredChannel="TAKEAWAY" />,
    ),
  },
  {
    path: '/delivery/order/:orderCode',
    element: withSuspense(<PublicRestaurantOrderingPage />),
  },
  {
    path: ROUTES.PUBLIC_FIDELITY_RECAP,
    element: withSuspense(<PublicWalletPage kind="FIDELITY_CARD" />),
  },
  {
    path: '/wallet/GIFT_CARD/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage />),
  },
  {
    path: '/wallet/gift-card/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage />),
  },
  {
    path: '/wallet/fidelity/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage kind="FIDELITY_CARD" />),
  },
  {
    path: '/wallet/FIDELITY_CARD/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage kind="FIDELITY_CARD" />),
  },
  {
    path: '/wallet/promo/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage kind="PROMO_COUPON" />),
  },
  {
    path: '/wallet/PROMO_COUPON/:code?/:publicToken?',
    element: withSuspense(<PublicWalletPage kind="PROMO_COUPON" />),
  },
  {
    path: '/order/supplier/:orderCode/managing',
    element: withSuspense(<PublicSupplierOrderPage />),
  },
  {
    path: '/supplier-order',
    element: withSuspense(<PublicSupplierOrderPage />),
  },
  {
    path: ROUTES.PUBLIC_BOOKING_FORM,
    element: withSuspense(
      <PublicBookingFormPage />,
      <PublicBookingFormSkeleton />,
    ),
  },
  {
    path: ROUTES.PUBLIC_PROMO_FORM,
    element: withSuspense(<PublicPromoPage />, <PublicPromoFormSkeleton />),
  },
  {
    path: '/cards/download/:token',
    element: withSuspense(<PublicCatalogCardPage />),
  },
  {
    path: '/cards/personal/:code',
    element: withSuspense(<PublicCatalogCardPage />),
  },
  {
    path: '/survery/:customerCode',
    element: withSuspense(<PublicSurveyPage />),
  },
  {
    path: ROUTES.PUBLIC_FIDELITY_FORM,
    element: withSuspense(
      <PublicFidelityPage />,
      <PublicFidelityFormSkeleton />,
    ),
  },
  {
    path: ROUTES.PUBLIC_LANDING,
    element: withSuspense(<PublicLandingPage />),
  },
  {
    path: ROUTES.PUBLIC_DATA_COLLECTION_FORM,
    element: withSuspense(
      <PublicDataCollectionFormPage />,
      <PublicDcfFormSkeleton />,
    ),
  },
  {
    path: ROUTES.PUBLIC_BOOKING_FORM_LEGACY,
    element: withSuspense(<LegacyPublicBookingFormRedirect />),
  },
  {
    path: ROUTES.PUBLIC_BOOKING_UPDATE_LEGACY,
    element: withSuspense(<LegacyPublicBookingUpdateRedirect />),
  },
  {
    path: ROUTES.PUBLIC_BOOKING_UPDATE,
    element: withSuspense(<PublicBookingUpdatePage />),
  },
  {
    path: ROUTES.PUBLIC_CARD_DETAILS,
    element: withSuspense(<PublicCardDetailsPage />),
  },
  {
    path: ROUTES.MENU_URL,
    element: withSuspense(<MenuPreviewPage />, <MenuPreviewSkeleton />),
  },
  {
    path: ROUTES.MENU_URL_LEGACY,
    element: withSuspense(<LegacyMenuRedirect />),
  },
  {
    path: ROUTES.MENU_URL_LEGACY_QUERY,
    element: withSuspense(<LegacyMenuRedirect />),
  },
  {
    path: ROUTES.MENU_PREVIEW,
    element: withSuspense(<MenuPreviewPage />, <MenuPreviewSkeleton />),
  },
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTES.LOGIN,
            element: withSuspense(<LoginPage />),
          },
        ],
      },
    ],
  },
  {
    element: <BackofficeGuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: ROUTES.BACKOFFICE_LOGIN,
            element: withSuspense(<BackofficeLoginPage />),
          },
        ],
      },
    ],
  },
  {
    element: <BackofficeAuthGuard />,
    children: [
      {
        element: <BackofficeLayout />,
        children: [
          {
            path: ROUTES.BACKOFFICE2020,
            element: withSuspense(<Backoffice2020Page />),
          },
          {
            path: ROUTES.BACKOFFICE2020_EDIT_ACTIVITY,
            element: withSuspense(<BackofficeEditActivityPage />),
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.UNAUTHORIZED,
    element: <UnauthorizedPage />,
  },
  {
    path: ROUTES.NOT_FOUND,
    element: <NotFoundPage />,
  },
];

export const router = createBrowserRouter(routes);
